/**
 * LocationConfirmBar — reusable "where are you posting from?" strip.
 *
 * Design contract:
 *   • On mount, ask browser for GPS → reverse-geocode via
 *     `/api/geocode/reverse` → show "You're posting from: City, State, Country".
 *   • User can tap "Change" to open 3 dropdowns:
 *       Country (static ISO-2 list, 240 countries)
 *       State  (free-text — no reliable global state DB, still useful)
 *       City   (type-to-search hitting `/api/geocode/city-suggest`
 *               scoped by the selected country)
 *   • On confirm, the parent gets `{country, country_code, state, city,
 *     lat, lng, formatted_address}` via `onConfirm(loc)`.
 *   • Also persists last confirmed location to `localStorage`
 *     (key `sp:last-post-location`) so the next post pre-fills it.
 *   • Deliberately does NOT touch the global LocalityContext — the
 *     user's set-location bar on top stays as-is.
 *
 * Props:
 *   • `onConfirm(loc)`   — required. Fires whenever the user confirms.
 *   • `label`             — optional. Small heading (default "Posting location").
 *   • `defaultLocation`   — optional. Skip GPS and use this seed instead.
 *   • `testid`            — optional. Suffix for data-testids so we can
 *                            distinguish multiple instances on one page.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { MapPin, ChevronDown, Check, RefreshCcw, Search, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { http } from "@/lib/api";
import COUNTRIES from "@/lib/countries";

const LS_KEY = "sp:last-post-location";

function _readLast() {
  try { return JSON.parse(localStorage.getItem(LS_KEY) || "null"); }
  catch { return null; }
}
function _writeLast(loc) {
  try { localStorage.setItem(LS_KEY, JSON.stringify(loc)); }
  catch { /* quota — ignore */ }
}

export default function LocationConfirmBar({
  onConfirm,
  label = "Posting location",
  defaultLocation = null,
  testid = "loc-confirm",
}) {
  const [loc,        setLoc]        = useState(defaultLocation || _readLast() || null);
  const [detecting,  setDetecting]  = useState(false);
  const [editing,    setEditing]    = useState(false);

  // Auto-detect once on mount, unless we have a defaultLocation or a
  // cached last-post-location.
  const didInit = useRef(false);
  useEffect(() => {
    if (didInit.current) return;
    didInit.current = true;
    if (loc) { onConfirm?.(loc); return; }   // pre-seed the parent
    detectGps();
    // eslint-disable-next-line
  }, []);

  const detectGps = () => {
    if (!("geolocation" in navigator)) {
      toast.info("This browser can't share GPS — pick your location manually.");
      setEditing(true);
      return;
    }
    setDetecting(true);
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        try {
          const { latitude: lat, longitude: lng } = pos.coords;
          const { data } = await http.get(`/geocode/reverse?lat=${lat}&lng=${lng}`);
          const widened = {
            country:      data.country || null,
            country_code: (data.country_code || "").toUpperCase() || null,
            state:        data.state || data.district || null,
            city:         data.city || null,
            lat, lng,
            formatted_address: data.formatted_address || null,
          };
          setLoc(widened);
          _writeLast(widened);
          onConfirm?.(widened);
        } catch (e) {
          toast.info("Couldn't identify your location automatically — pick manually.");
          setEditing(true);
        } finally { setDetecting(false); }
      },
      () => {
        setDetecting(false);
        toast.info("Location permission declined — pick manually.");
        setEditing(true);
      },
      { enableHighAccuracy: false, timeout: 10000 },
    );
  };

  const humanReadable = useMemo(() => {
    if (!loc) return null;
    return [loc.city, loc.state, loc.country].filter(Boolean).join(", ");
  }, [loc]);

  const commit = (next) => {
    setLoc(next);
    _writeLast(next);
    setEditing(false);
    onConfirm?.(next);
    toast.success("Post location confirmed");
  };

  return (
    <div className="rounded-2xl border p-3 sm:p-4"
         style={{ borderColor: "var(--wofy-border)", background: "#F8FAFC" }}
         data-testid={testid}>
      <div className="flex items-center gap-2 mb-1">
        <MapPin className="w-4 h-4" style={{ color: "#0ea5e9" }} />
        <div className="text-[11px] font-mono uppercase tracking-wider opacity-70">{label}</div>
      </div>

      {/* Detected / current row */}
      {!editing && (
        <div className="flex items-center gap-2 flex-wrap" data-testid={`${testid}-summary`}>
          {detecting ? (
            <div className="inline-flex items-center gap-2 text-sm opacity-80">
              <Loader2 className="w-4 h-4 animate-spin" /> Detecting your location…
            </div>
          ) : loc ? (
            <>
              <div className="text-sm">
                <span className="opacity-70">You're posting from</span>
                <span className="font-medium ml-1" data-testid={`${testid}-current`}>
                  {humanReadable || "somewhere on earth"}
                </span>
                {loc.lat != null && loc.lng != null && (
                  <span className="opacity-50 font-mono text-[11px] ml-2">
                    · {loc.lat.toFixed(3)}, {loc.lng.toFixed(3)}
                  </span>
                )}
              </div>
              <div className="ml-auto flex items-center gap-1.5">
                <button type="button" onClick={() => setEditing(true)}
                        className="wofy-btn-ghost text-xs"
                        data-testid={`${testid}-change`}>
                  <RefreshCcw className="w-3 h-3" /> Is this right? Change
                </button>
              </div>
            </>
          ) : (
            <div className="flex items-center gap-2">
              <div className="text-sm opacity-80">No location set yet.</div>
              <button type="button" onClick={detectGps} className="wofy-btn-ghost text-xs">
                <MapPin className="w-3 h-3" /> Use my location
              </button>
              <button type="button" onClick={() => setEditing(true)} className="wofy-btn-ghost text-xs">
                Pick manually
              </button>
            </div>
          )}
        </div>
      )}

      {/* Editor */}
      {editing && (
        <LocationEditor initial={loc}
                        testid={testid}
                        onCancel={() => setEditing(false)}
                        onCommit={commit} />
      )}
    </div>
  );
}


/* ─── Location editor (3 stacked selectors) ─────────────────────── */
function LocationEditor({ initial, testid, onCancel, onCommit }) {
  const [country,     setCountry]     = useState(initial?.country_code || "");   // ISO alpha-2
  const [countryName, setCountryName] = useState(initial?.country || "");
  const [state,       setState]       = useState(initial?.state || "");
  const [cityQ,       setCityQ]       = useState(initial?.city || "");
  const [suggestions, setSuggestions] = useState([]);
  const [suggBusy,    setSuggBusy]    = useState(false);
  const [chosenCity,  setChosenCity]  = useState(initial ? {
    city:  initial.city, state: initial.state, country: initial.country,
    country_code: initial.country_code, lat: initial.lat, lng: initial.lng,
  } : null);

  // Country → keep name synced with the ISO code.
  useEffect(() => {
    if (!country) { setCountryName(""); return; }
    const hit = COUNTRIES.find((c) => c.code === country);
    if (hit) setCountryName(hit.name);
  }, [country]);

  // Debounced city autocomplete.
  useEffect(() => {
    const q = cityQ.trim();
    if (q.length < 2) { setSuggestions([]); return; }
    const t = setTimeout(async () => {
      setSuggBusy(true);
      try {
        const params = new URLSearchParams({ q, limit: "10" });
        if (country) params.set("country", country);
        const { data } = await http.get(`/geocode/city-suggest?${params.toString()}`);
        setSuggestions(data.results || []);
      } catch { setSuggestions([]); }
      finally { setSuggBusy(false); }
    }, 250);
    return () => clearTimeout(t);
  }, [cityQ, country]);

  const pickSuggestion = (s) => {
    setChosenCity(s);
    setCityQ(s.city || "");
    if (s.state) setState(s.state);
    if (s.country && !countryName) setCountryName(s.country);
    if (s.country_code && !country) setCountry(s.country_code);
    setSuggestions([]);
  };

  const canConfirm = !!(country || countryName) && !!cityQ.trim();

  const submit = () => {
    if (!canConfirm) return;
    const finalLoc = {
      country:      countryName || chosenCity?.country || null,
      country_code: country || chosenCity?.country_code || null,
      state:        state?.trim() || chosenCity?.state || null,
      city:         chosenCity?.city || cityQ.trim(),
      lat:          chosenCity?.lat ?? initial?.lat ?? null,
      lng:          chosenCity?.lng ?? initial?.lng ?? null,
      formatted_address: chosenCity?.display || null,
    };
    onCommit(finalLoc);
  };

  return (
    <div className="mt-2 space-y-2" data-testid={`${testid}-editor`}>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
        {/* Country */}
        <label className="flex flex-col text-xs">
          <span className="opacity-70 mb-1">Country</span>
          <div className="relative">
            <select value={country} onChange={(e) => setCountry(e.target.value)}
                    className="w-full appearance-none px-3 py-2 pr-8 rounded-lg border text-sm bg-white"
                    style={{ borderColor: "var(--wofy-border)" }}
                    data-testid={`${testid}-country`}>
              <option value="">Select…</option>
              {COUNTRIES.map((c) => (
                <option key={c.code} value={c.code}>{c.name}</option>
              ))}
            </select>
            <ChevronDown className="w-3 h-3 absolute right-2 top-1/2 -translate-y-1/2 opacity-50 pointer-events-none" />
          </div>
        </label>

        {/* State / Province */}
        <label className="flex flex-col text-xs">
          <span className="opacity-70 mb-1">State / Province</span>
          <input value={state} onChange={(e) => setState(e.target.value)}
                 placeholder="e.g. California, Kerala"
                 className="w-full px-3 py-2 rounded-lg border text-sm"
                 style={{ borderColor: "var(--wofy-border)" }}
                 data-testid={`${testid}-state`} />
        </label>

        {/* City with typeahead */}
        <label className="flex flex-col text-xs relative">
          <span className="opacity-70 mb-1">City / Village</span>
          <div className="relative">
            <Search className="w-3 h-3 absolute left-3 top-1/2 -translate-y-1/2 opacity-50" />
            <input value={cityQ}
                   onChange={(e) => { setCityQ(e.target.value); setChosenCity(null); }}
                   placeholder="Type to search…"
                   className="w-full pl-8 pr-3 py-2 rounded-lg border text-sm"
                   style={{ borderColor: "var(--wofy-border)" }}
                   data-testid={`${testid}-city`} />
            {suggBusy && (
              <Loader2 className="w-3 h-3 animate-spin absolute right-2 top-1/2 -translate-y-1/2 opacity-50" />
            )}
          </div>
          {suggestions.length > 0 && (
            <ul className="absolute z-30 mt-14 left-0 right-0 bg-white border rounded-lg shadow-md max-h-56 overflow-auto"
                style={{ borderColor: "var(--wofy-border)" }}
                data-testid={`${testid}-suggestions`}>
              {suggestions.map((s, i) => (
                <li key={`${s.city}-${s.state}-${s.country_code}-${i}`}>
                  <button type="button" onClick={() => pickSuggestion(s)}
                          className="w-full text-left px-3 py-1.5 hover:bg-black/5 text-xs">
                    <div className="font-medium">{s.city}</div>
                    <div className="opacity-70">{[s.state, s.country].filter(Boolean).join(", ")}</div>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </label>
      </div>

      {/* Actions */}
      <div className="flex items-center gap-2 flex-wrap">
        <button type="button" onClick={submit} disabled={!canConfirm}
                className="wofy-btn-primary text-sm"
                data-testid={`${testid}-confirm`}>
          <Check className="w-4 h-4" /> Confirm this location
        </button>
        <button type="button" onClick={onCancel} className="wofy-btn-ghost text-xs">
          Cancel
        </button>
        <div className="ml-auto text-[11px] opacity-60">
          Your global "set your location" bar isn't affected by this choice.
        </div>
      </div>
    </div>
  );
}
